import { darken, lighten, parseHex, HOVER_DARKEN_AMOUNT } from './colors';
import {
  BRAND_ACCENT,
  BRAND_ACCENT_TEXT,
  BRAND_FONT_STACK,
  BRAND_LINK,
  MUTED_LIGHTEN_AMOUNT,
  SUBTLE_LIGHTEN_AMOUNT,
  clampBorderRadius,
} from './brand';

/**
 * The subset of `WidgetConfig` the modal's accent colours are derived from. Narrower than
 * the full config (rather than importing `WidgetConfig` itself) so `Modal` can stay usable
 * in tests/call sites that don't care about appearance — see `Modal.tsx`'s optional `config`
 * prop, which defaults to `{}` (every field falls back to the original hardcoded indigo).
 */
export interface AccentConfig {
  bgColor?: string | null;
  textColor?: string | null;
  hoverBgColor?: string | null;
  borderRadius?: number | null;
  fontFamily?: string | null;
  linkColor?: string | null;
}

/**
 * Validates a config colour as a real `#rgb`/`#rrggbb` hex string, returning `null` otherwise.
 * Two independent reasons this matters here (neither applies to `Button.tsx`, which only ever
 * hands colours to inline `style` props, where a browser silently drops an invalid value):
 *
 *  1. Every colour field on `UpdateApplicationDto` is validated as only `z.string().min(1)` —
 *     no hex-shape check — so an arbitrary string (including one containing `}`) can reach this
 *     module's callers. Interpolating it unvalidated into the CSS text block below would let
 *     that string break out of the `.ft-overlay { ... }` rule and corrupt the rest of the
 *     widget's stylesheet for that Application's end users.
 *  2. `darken`/`lighten` both return non-hex input unchanged (by design, so they never throw).
 *     Without this guard, a non-hex `bgColor` would flow straight into `--ft-accent` verbatim
 *     while also being fed to `lighten` for `--ft-accent-muted`/`--ft-accent-subtle`, which
 *     would each just echo the same unrecognised string back — collapsing `--ft-accent-subtle`
 *     onto `--ft-accent` and rendering the attachment chip as same-colour text on same-colour
 *     background.
 */
export function validHex(value: string | null | undefined): string | null {
  if (value == null) return null;
  return parseHex(value) ? value : null;
}

/**
 * Font stacks are free text on the DTO (`z.string().min(1)`), so a value
 * containing `}`, `;`, or a comment sequence could terminate the declaration
 * and inject arbitrary rules into the widget's stylesheet. Only family names,
 * quotes, spaces, commas, digits and hyphens are allowed through; anything else
 * falls back to the brand stack.
 */
const SAFE_FONT_STACK = /^[A-Za-z0-9\s,'"-]+$/;

export function validFontStack(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed && SAFE_FONT_STACK.test(trimmed) ? trimmed : null;
}

/**
 * Builds the CSS custom-property declaration block for the modal's accent colours, scoped to
 * `.ft-overlay` — `Modal`'s own root element, which only ever exists inside the widget's closed
 * shadow root (see `mount.ts`). Never declared on `:root`/`document.documentElement`, so nothing
 * leaks onto the host page.
 *
 * `--ft-accent-muted`/`--ft-accent-subtle` are only ever declared when a *validly configured*
 * `bgColor` is present. Both are derived tints of the accent (via `lighten`), not literal
 * passthroughs.
 *
 * The `if (configuredBg)` guard below is deliberate: with no real `bgColor` to derive from,
 * omitting the declarations lets `styles.ts`'s own `var(--ft-accent-muted, …)` /
 * `var(--ft-accent-subtle, …)` fallbacks govern. Those fallbacks are `BRAND_ACCENT_MUTED` /
 * `BRAND_ACCENT_SUBTLE` — `lighten(BRAND_ACCENT, …)` at the SAME two ratios this function
 * uses — so the configured and unconfigured paths agree by construction rather than by
 * coincidence, and an unconfigured Application renders the brand tint rather than a colour
 * nobody chose. Emitting the declarations here unconditionally would work too, but it would
 * make `styles.ts`'s fallbacks dead code that silently drifts.
 */
export function buildAccentStyle(config: AccentConfig): string {
  const configuredBg = validHex(config.bgColor);
  const configuredText = validHex(config.textColor);
  const configuredHover = validHex(config.hoverBgColor);

  const accent = configuredBg ?? BRAND_ACCENT;
  const accentText = configuredText ?? BRAND_ACCENT_TEXT;
  const accentHover = configuredHover ?? darken(accent, HOVER_DARKEN_AMOUNT);

  const declarations = [
    `--ft-accent: ${accent};`,
    `--ft-accent-text: ${accentText};`,
    `--ft-accent-hover: ${accentHover};`,
    `--ft-radius: ${clampBorderRadius(config.borderRadius)}px;`,
    `--ft-font: ${validFontStack(config.fontFamily) ?? BRAND_FONT_STACK};`,
    `--ft-link: ${validHex(config.linkColor) ?? BRAND_LINK};`,
  ];

  if (configuredBg) {
    declarations.push(`--ft-accent-muted: ${lighten(accent, MUTED_LIGHTEN_AMOUNT)};`);
    declarations.push(`--ft-accent-subtle: ${lighten(accent, SUBTLE_LIGHTEN_AMOUNT)};`);
  }

  return `.ft-overlay {\n  ${declarations.join('\n  ')}\n}`;
}
